import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import { MessageCircle } from 'lucide-react';
import moment from 'moment/moment';
import classnames from 'classnames';
import { StoreInterface } from '../../store/types';

interface Props {
  className?: string;
}

export default function WhatsAppSenderNote(props: Props) {
  const { className } = props;

  const { t } = useTranslation(null, { keyPrefix: 'Components:Auth:OtpFlow:sender_note' });
  const { company } = useSelector((state: StoreInterface) => state.initialData);

  const sender = company?.service_provider?.name ?? 'FoodAmigos';
  const circleLogoUrl = company?.service_provider?.circle_logo_url;
  const initials = sender.slice(0, 2).toUpperCase();
  const time = useMemo(() => moment().format('HH:mm'), []);

  return (
    <div className={classnames(container, className)} data-test="otp-whatsapp-sender-note">
      <p className="text-center text-xs text-muted-foreground">
        <Trans
          t={t}
          i18nKey="disclaimer"
          values={{ sender }}
          components={{ strong: <span className="font-semibold text-foreground" /> }}
        />
      </p>

      <div className={card}>
        {/* Header */}
        <div className={header}>
          <span className={avatar}>
            {circleLogoUrl ? <img src={circleLogoUrl} alt={sender} className={avatarImage} /> : initials}
          </span>
          <div className="flex flex-col leading-tight">
            <span className="text-xs font-semibold">{sender}</span>
            <span className="text-[10px] opacity-80">{t('business_account')}</span>
          </div>
          <MessageCircle className="ml-auto h-3.5 w-3.5 opacity-80" />
        </div>

        {/* Message bubble */}
        <div className="px-3 py-3">
          <div className={bubble}>
            <p className="text-[11px] leading-snug text-foreground">
              <Trans t={t} i18nKey="message" components={{ strong: <span className="font-bold tracking-wider" /> }} />
            </p>
            <p className="mt-1 text-right text-[9px] text-muted-foreground">
              {time} <span className="text-[#34B7F1]">✓✓</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

const container = 'space-y-2 text-left';
const card = 'mx-auto max-w-[280px] overflow-hidden rounded-2xl border bg-[#ECE5DD] shadow-sm';
const header = 'flex items-center gap-2 bg-[#075E54] px-3 py-2 text-white';
const avatar = 'grid h-7 w-7 place-items-center rounded-full bg-white/20 text-[10px] font-bold';
const avatarImage = 'h-full w-full rounded-full object-cover';
const bubble = 'relative max-w-[85%] rounded-lg bg-white px-3 py-2 shadow-sm';
